import type { ReactNode } from 'react';

import DataGrid from '@/components/ui/data-grid';
import type {
  DataGridActionItem,
  DataGridColumnDef,
  DataGridListItemContext,
  HeaderAction,
} from '@/components/ui/data-grid';
import DeleteDialog from '@/components/ui/delete-dialog';
import LoadingContainer from '@/components/ui/loading-container';

export type ResourceTableDestructiveDialog<T> = {
  item: T | null;
  title: (item: T) => string;
  description: string;
  confirmText?: string;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
  isLoading: boolean;
};

export type ResourceTableProps<T> = {
  /** Array of data objects to display */
  data: T[];
  /** Whether data is being loaded */
  isLoading: boolean;

  /** Column definitions */
  columns: DataGridColumnDef<T>[];
  /** Row actions (inline icon buttons) */
  actions?: (item: T) => DataGridActionItem<T>[];
  /** Makes rows clickable — returns relative href for navigation */
  rowHref?: (item: T) => string;

  /** Mobile-responsive row renderer */
  renderListItem: (
    row: T,
    index: number,
    context: DataGridListItemContext
  ) => ReactNode;

  /** Header action buttons (e.g. "Create", "Download") */
  headerActions?: HeaderAction[];

  /** Empty state content */
  emptyState: { title: string; description: string };

  /** Optional destructive confirmation dialog (delete, revoke, etc.) */
  destructiveDialog?: ResourceTableDestructiveDialog<T>;

  /** Actions column header label (defaults to empty string) */
  actionsLabel?: string;
};

/**
 * Shared composition wrapper for resource list pages.
 * Standardizes the loading → grid → destructive-dialog layout
 * so secrets, clients, and future resource tables share identical scaffolding.
 */
export default function ResourceTable<T>({
  data,
  isLoading,
  columns,
  actions,
  rowHref,
  renderListItem,
  headerActions,
  emptyState,
  destructiveDialog,
  actionsLabel = '',
}: ResourceTableProps<T>) {
  if (isLoading) {
    return <LoadingContainer />;
  }

  return (
    <div>
      <DataGrid
        data={data}
        columns={columns}
        actions={actions}
        rowHref={rowHref ? (row) => rowHref(row) : undefined}
        renderListItem={renderListItem}
        emptyState={emptyState}
        headerActions={headerActions}
        actionsLabel={actionsLabel}
      />

      {destructiveDialog && (
        <DeleteDialog
          isOpen={!!destructiveDialog.item}
          title={
            destructiveDialog.item
              ? destructiveDialog.title(destructiveDialog.item)
              : ''
          }
          description={destructiveDialog.description}
          confirmText={destructiveDialog.confirmText}
          onClose={destructiveDialog.onClose}
          onConfirm={destructiveDialog.onConfirm}
          isLoading={destructiveDialog.isLoading}
        />
      )}
    </div>
  );
}
